import React, { useState, useMemo, useEffect } from "react";
import BreadCrumb from "../Common/BreadCrumb";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
} from "react-icons/fi";
import ArticleAddEditModal from "../../Components/adminComponents/DashboardArticleComponents/ArticleAddEditModal";

const DashboardArticle = () => {
  const initialArticles = [
    {
      id: 1,
      image:
        "https://images.unsplash.com/photo-1502877338535-766e1452684a?w=1200&q=80",
      title: "A Ride Through The Hills",
      description:
        "An adventurous day riding through winding roads, beautiful views and unexpected stops.",
      category: "Travel",
      posted: "2025-11-20",
    },
    {
      id: 2,
      image:
        "https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=1200&q=80",
      title: "Maintaining Your Bike Like a Pro",
      description:
        "Simple maintenance tips that keep your bike running smoothly and safely.",
      category: "Tips",
      posted: "2025-11-10",
    },
    {
      id: 3,
      image:
        "https://images.unsplash.com/photo-1525609004556-c46c7d6cf023?w=1200&q=80",
      title: "Best Winter Routes",
      description:
        "Top scenic routes to ride during winter — what to expect and what to pack.",
      category: "Guides",
      posted: "2025-10-28",
    },
    {
      id: 4,
      image:
        "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=1200&q=80",
      title: "How To Photograph Your Ride",
      description:
        "Camera settings and composition tips to capture stunning riding photos.",
      category: "Photography",
      posted: "2025-09-14",
    },
  ];

  const [articles, setArticles] = useState(initialArticles);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const postPerPage = 6;

  const filtered = useMemo(() => {
    if (!query) return articles;
    const q = query.toLowerCase();
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
    );
  }, [articles, query]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / postPerPage));
  const paginated = useMemo(
    () => filtered.slice((page - 1) * postPerPage, page * postPerPage),
    [filtered, page]
  );

  function openAddModal() {
    setEditingArticle(null);
    setIsModalOpen(true);
  }

  function openEditModal(article) {
    setEditingArticle(article);
    setIsModalOpen(true);
  }

  function handleSaveFromModal(form) {
    if (editingArticle) {
      setArticles((prev) =>
        prev.map((a) => (a.id === editingArticle.id ? { ...a, ...form } : a))
      );
    } else {
      const id = Math.max(0, ...articles.map((a) => a.id)) + 1;
      setArticles((prev) => [{ id, ...form }, ...prev]);
      setPage(1);
    }

    setIsModalOpen(false);
    setEditingArticle(null);
  }

  function confirmDelete(article) {
    setArticleToDelete(article);
    setIsDeleteOpen(true);
  }

  function handleDelete() {
    if (!articleToDelete) return;
    setArticles((prev) => prev.filter((a) => a.id !== articleToDelete.id));
    setIsDeleteOpen(false);
    setArticleToDelete(null);
  }

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [totalPages, page]);

  return (
    <div className="p-5">
      <BreadCrumb
        title="Dashboard Articles"
        trail={["Dashboard", "Articles"]}
      />

      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-sm text-slate-500">
          Manage blog articles — add, edit, delete and organize posts.
        </p>

        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400">
              <FiSearch />
            </div>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search title or category"
              className="pl-10 pr-4 py-2 rounded-lg border border-slate-200 shadow-sm w-96 focus:outline-none focus:ring-2 focus:ring-[#5989C8] focus:border-transparent"
            />
          </div>

          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#5989C8] to-[#064585] text-white px-4 py-2 rounded-xl shadow-lg transform hover:-translate-y-0.5 transition"
          >
            <FiPlus /> Add Article
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {paginated.map((article) => (
          <div
            key={article.id}
            className="bg-white rounded-2xl p-4 shadow-md border border-transparent hover:shadow-xl hover:-translate-y-1 transition transform overflow-hidden"
          >
            <div className="h-40 w-full rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex items-start justify-between gap-3 mt-3">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-slate-900">
                  {article.title.length > 25
                    ? article.title.slice(0, 25) + "..."
                    : article.title}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {article.category}
                </p>
                <p className="mt-3 text-sm text-slate-700 line-clamp-3">
                  {article.description.length > 100
                    ? article.description.slice(0, 100) + "..."
                    : article.description}
                </p>
              </div>

              <div className="flex flex-col items-end gap-2">
                <div className="text-xs text-slate-400">{article.posted}</div>
                <div className="flex flex-col items-center gap-2 bg-slate-50 p-1 rounded-lg">
                  <button
                    onClick={() => openEditModal(article)}
                    title="Edit"
                    className="p-2 rounded hover:bg-slate-100"
                  >
                    <FiEdit />
                  </button>
                  <button
                    onClick={() => confirmDelete(article)}
                    title="Delete"
                    className="p-2 rounded hover:bg-slate-100 text-red-600"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {paginated.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            No articles found. Try adjusting your search or add a new article.
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <div>
            Showing{" "}
            <strong className="text-slate-900">
              {Math.min((page - 1) * postPerPage + 1, total)}
            </strong>{" "}
            -{" "}
            <strong className="text-slate-900">
              {Math.min(page * postPerPage, total)}
            </strong>{" "}
            of <strong className="text-slate-900">{total}</strong>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`p-2 rounded ${
              page === 1
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-slate-100"
            }`}
          >
            <FiChevronLeft />
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`h-8 w-8 rounded-full ${
                  page === i + 1
                    ? "bg-gradient-to-r from-[#5989C8] to-[#064585] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`p-2 rounded ${
              page === totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-slate-100"
            }`}
          >
            <FiChevronRight />
          </button>
        </div>
      </div>

      <ArticleAddEditModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingArticle(null);
        }}
        onSave={handleSaveFromModal}
        initialData={editingArticle}
      />

      {/* Delete  */}
      {isDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg ring-1 ring-slate-100">
            <h3 className="text-lg font-semibold">Delete Article</h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete{" "}
              <strong>{articleToDelete?.title}</strong>? This action cannot be
              undone.
            </p>

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteOpen(false)}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardArticle;
